import * as React from 'react';
import { useSessionExpired } from '@/hooks/use-session-expired';
import { usePollErrorToast } from '@/hooks/use-poll-error-toast';
import { isSessionExpired, formatApiError } from '@/lib/api';
import { AdminNotificationStack } from '@/components/admin/admin-notification-stack';
import { useAuth } from '@/contexts/auth-context';
import { mapApiAdminNotificationsToStackItems } from '@/lib/map-api-admin-notification';
import {
  fetchActiveAdminNotifications,
  resolveAdminNotification,
} from '@/lib/notifications';
import { ADMIN_NOTIFICATIONS_REFRESH_EVENT } from '@/lib/admin-notifications-refresh';
import type { AdminNotificationStackItem } from '@/lib/admin-notification-stack.types';
import { canEditSettings, canViewSettings } from '@/lib/settings-admin';
import { toast } from '@/hooks/use-toast';

export function AdminNotifications() {
  const { user } = useAuth();
  const expired = useSessionExpired();
  const { report: reportPollError, reset: resetPollError } = usePollErrorToast('Unable to load notifications');
  const canLoad = canViewSettings(user);
  const canSnooze = canEditSettings(user);
  const [items, setItems] = React.useState<AdminNotificationStackItem[]>([]);
  const [resolvingIds, setResolvingIds] = React.useState<Set<string>>(new Set());

  async function load() {
    if (!canViewSettings(user) || expired || isSessionExpired()) return;
    try {
      const rows = await fetchActiveAdminNotifications();
      resetPollError();
      setItems(mapApiAdminNotificationsToStackItems(rows));
    } catch (err) {
      const message = formatApiError(err, 'Request failed');
      // Operators and other non-settings roles must not see a permission toast on every poll.
      if (/Insufficient permissions|403/i.test(message)) return;
      reportPollError(err);
    }
  }

  React.useEffect(() => {
    if (!canLoad || expired) return;
    void load();
    const id = window.setInterval(() => void load(), 60_000);
    const onRefresh = () => { void load(); };
    window.addEventListener(ADMIN_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(ADMIN_NOTIFICATIONS_REFRESH_EVENT, onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, expired]);

  async function handleResolve(id: string, item: AdminNotificationStackItem) {
    setResolvingIds((prev) => new Set(prev).add(id));
    try {
      await resolveAdminNotification(id);
      setItems((current) => current.filter((row) => row.id !== id));
      toast({ title: 'Notification snoozed', description: item.message });
    } catch (err) {
      toast({
        title: 'Could not snooze notification',
        description: formatApiError(err, 'Request failed'),
        variant: 'destructive',
      });
    } finally {
      setResolvingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  if (!canLoad) return null;

  return (
    <AdminNotificationStack
      notifications={items}
      onResolve={canSnooze ? handleResolve : undefined}
      canSnooze={canSnooze}
      resolvingIds={resolvingIds}
    />
  );
}
